import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import CircleContainerizationView from "components/shared/CircleContainerizationView";
import { Link } from "react-router-dom";
import { searchByProp } from "utils/search";
import ForecastProductView from "./ForecastProductView";
import { FETCH_FCST_OVERVIEW } from "./graphql";

const ForecastTableView = ({ mode, q, onlyFcst }) => {
  const stickyHeaderBgClass =
    "bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80";
  const stickyBodyBgClass = "bg-gray-50 dark:bg-gray-800";

  const { loading, error, data } = useQuery(FETCH_FCST_OVERVIEW, {
    variables: { mode },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const parsedForecast =
    typeof data.salesGetForecast === "string"
      ? JSON.parse(data.salesGetForecast)
      : data.salesGetForecast || {};

  const monthsToShow = parsedForecast.months;
  const forecastColumns = parsedForecast.forecast_categories.map((item) => ({
    id: String(item.id),
    name: item.name || String(item.id),
    mode: item.mode,
  }));

  console.log("parsedForecast", parsedForecast);

  const rows = parsedForecast.products.map((item) => {
    const product = item.product || {};
    return {
      product,
      cells: item.cells || {},
      total: Number(item.total || 0),
    };
  });

  const products = rows.filter((row) => {
    if (!searchByProp(row.product, ["number", "name"], q)) return false;
    if (!onlyFcst) return true;
    return row.total > 0;
  });

  const euContainers = data.euContainers;
  const usContainers = data.usContainers;

  let shippingZones = ["EU", "US"];
  let containerGroups = [euContainers, usContainers];
  if (mode === "INC") {
    shippingZones = ["US"];
    containerGroups = [usContainers];
  }
  if (mode === "AB") {
    shippingZones = ["EU"];
    containerGroups = [euContainers];
  }

  let monthTotals = {};
  monthsToShow.forEach((month) => {
    monthTotals[month] = {};
    forecastColumns.forEach((column) => {
      let totalQty = 0;
      let totalCbm = 0;

      rows.forEach((row) => {
        const qty = Number(row.cells[month]?.[column.id] || 0);
        const cbmPerCarton = Number(row.product?.cbm || 0);
        const itemsPerSet = Number(row.product?.itemsPerSet || 1);
        const totalCartons = qty / itemsPerSet;
        const rowCbm = totalCartons * cbmPerCarton;
        totalQty += qty;
        totalCbm += rowCbm;
      });
      monthTotals[month][column.id] = { totalQty, totalCbm };
    });
  });

  return (
    <table className="w-full">
      <thead className="sticky top-0 z-20 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80">
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th
            rowSpan={2}
            className={`sticky left-0 z-30 px-4 py-3 text-left whitespace-nowrap align-middle border-r border-gray-200 dark:border-gray-700 ${stickyHeaderBgClass}`}
          >
            Product ({products.length})
          </th>
          {monthsToShow.map((month) => (
            <th
              key={month}
              colSpan={forecastColumns.length}
              className="px-2 pt-3 pb-1 text-right whitespace-nowrap border-x border-gray-200 dark:border-gray-700"
            >
              {month}
            </th>
          ))}
          <th
            rowSpan={2}
            className={`sticky right-0 z-30 px-4 py-3 text-right whitespace-nowrap align-middle border-l border-gray-200 dark:border-gray-700 ${stickyHeaderBgClass}`}
          >
            Total
          </th>
        </tr>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          {monthsToShow.flatMap((month) =>
            forecastColumns.map((column) => {
              return (
                <th
                  key={`${month}-${column.id}`}
                  className="text-right whitespace-nowrap border-x border-gray-200 dark:border-gray-700"
                >
                  {mode === "ALL" ? (
                    column.name
                  ) : (
                    <Link
                      to={`${month}/${column.id}?q=${q}&onlyFcst=${onlyFcst}`}
                    >
                      {column.name}
                    </Link>
                  )}
                </th>
              );
            }),
          )}
        </tr>
      </thead>
      <tbody>
        {parsedForecast.products.length === 0 && (
          <tr>
            <td
              colSpan={2 + monthsToShow.length * forecastColumns.length}
              className="p-8 text-center"
            >
              No submitted forecast data at the moment. Please go to the{" "}
              <Link to="categories">Forecast Categories Page</Link> to submit
              your forecast by category and month.
            </td>
          </tr>
        )}

        {products.map((row) => {
          const { product, cells, total } = row;
          return (
            <tr
              key={product.id}
              className="border-y border-gray-100 dark:border-gray-700"
            >
              <td
                className={`sticky left-0 z-10 px-4 py-2 border-r border-gray-100 dark:border-gray-700 ${stickyBodyBgClass}`}
              >
                <ForecastProductView product={product} />
              </td>
              {monthsToShow.flatMap((month) =>
                forecastColumns.map((column) => (
                  <td
                    key={`${month}-${column.id}`}
                    className="px-2 py-2 text-right whitespace-nowrap border-x border-gray-100 dark:border-gray-700"
                  >
                    {Number(cells[month]?.[column.id] || 0).toLocaleString()}
                  </td>
                )),
              )}
              <td
                className={`sticky right-0 z-10 px-4 py-2 text-right font-semibold whitespace-nowrap border-l border-gray-100 dark:border-gray-700 ${stickyBodyBgClass}`}
              >
                {total.toLocaleString()}
              </td>
            </tr>
          );
        })}
      </tbody>

      <tfoot>
        <tr className="sticky bottom-0 backdrop-blur bg-gray-200 bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90 border-t border-gray-300 dark:border-gray-600 z-20">
          <td className="sticky left-0 z-30 px-4 py-3 text-left border-r border-gray-300 dark:border-gray-600 bg-gray-200 bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90">
            <div className="font-semibold">Total CBM and Container Cost</div>
            <div className="mt-1">
              {containerGroups.map((containers, index) => (
                <div key={index} className="font-normal">
                  <label>{shippingZones[index]}:</label>
                  {containers
                    .map((c) => `${c.name}: $${c.cost.toLocaleString()}`)
                    .join(", ")}
                </div>
              ))}
            </div>
          </td>

          {monthsToShow.flatMap((month) =>
            forecastColumns.map((column) => {
              return (
                <td
                  key={`forecast-cbm-${month}-${column.id}`}
                  className="px-2 py-3 text-right whitespace-nowrap"
                >
                  {month ? (
                    <CircleContainerizationView
                      showCost
                      totalQty={monthTotals[month][column.id].totalQty}
                      totalCbm={monthTotals[month][column.id].totalCbm}
                      shippingZone={column.mode === "INC" ? "US" : "EU"}
                      defaultDisabledContainers={["40GP"]}
                      displayTotalCbm={monthTotals[month][
                        column.id
                      ].totalCbm.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 3,
                      })}
                      className="text-right"
                    />
                  ) : (
                    "-"
                  )}
                </td>
              );
            }),
          )}
          <th></th>
        </tr>
      </tfoot>
    </table>
  );
};

export default ForecastTableView;
